/**
 * @file        src/lib/abVariant.ts
 * @description Sticky A/B variant assignment for the browser: a visitor is
 *              assigned one variant per test on first hit and keeps it on
 *              every later render and every later page. Generic by design —
 *              the first consumer is the lead form (series home-stage-b),
 *              but nothing here knows what a form is.
 * @runs        Browser (SSR-safe via a window guard — see @rule SSR IS
 *              VARIANT ZERO).
 * @universal   getVariant / forceVariant — the ONE A/B assignment mechanism
 *              of the project. Registered in knowledge/universals/tools.md.
 *              Accepts: testId, variants[]. A call site must never roll its
 *              own Math.random() for a variant; it calls getVariant.
 * @rule        SSR IS VARIANT ZERO. On the server there is no window and no
 *              storage, so getVariant returns variants[0]. The first entry
 *              of the list MUST therefore be the control variant whose
 *              markup is safe to render before hydration (series
 *              home-stage-b, invariant B-I6).
 * @rule        STORAGE IS BEST-EFFORT, ASSIGNMENT IS NOT. localStorage
 *              throws in private mode and in embedded browsers with site
 *              data turned off. Every access is wrapped; on failure the
 *              assignment falls back to an in-memory map that lives as long
 *              as the page does. Such a visitor keeps the variant within the
 *              page but may get another one on the next page — the funnel
 *              goals carry the variant per page view, so the report stays
 *              readable either way.
 * @rule        AN UNKNOWN STORED VALUE IS RE-ROLLED, NOT KEPT. When a test's
 *              list changes (a losing variant is dropped), a stored value
 *              that is no longer offered must not leak into reports: that
 *              visitor is re-assigned from the current list.
 * @rule        NEVER THROW. This runs inside render paths. A storage or URL
 *              failure must be invisible to the visitor, exactly like
 *              trackGoal in src/lib/metrika.ts.
 * @rule        THE ASSIGNMENT IS MIRRORED INTO A COOKIE (`ab_<testId>`) so a
 *              SERVER component can render the assigned variant directly.
 *              Without it the server can only render the control and swap
 *              after hydration, which moves everything below the form — on
 *              the homepage the lead form differs by ~300 px between
 *              variants, so that swap is a visible jump, and reserving the
 *              tallest variant's height instead leaves the same ~300 px of
 *              empty space under the shorter ones. The cookie carries no
 *              personal data: one of four literals, readable by nobody but
 *              this site.
 * @rule        THE COOKIE IS ALSO READ, NOT ONLY WRITTEN (series
 *              home-stage-b, step B04e). The two stores can disagree — a
 *              visitor who clears site data keeps a cookie whose lifetime the
 *              site controls, and a browser can refuse one store and allow
 *              the other. Whenever they do disagree, the homepage renders the
 *              cookie's variant on the SERVER while the tails would roll a
 *              fresh one from empty storage, and the visitor meets two
 *              different forms on two pages — exactly what invariant B-I6
 *              forbids. So localStorage is read first and the cookie is the
 *              fallback, and whichever one answers is mirrored into the
 *              other. Only a browser with neither rolls a new variant.
 */

// @rule One storage prefix for every A/B test of the project. The full key
//       is `ab:<testId>`; nothing else writes under this prefix.
const STORAGE_PREFIX = "ab:";

// Session fallback used when localStorage is unavailable (private mode,
// site data blocked). Lives for the lifetime of the page.
const memoryStore = new Map<string, string>();

function storageKeyFor(testId: string): string {
  return `${STORAGE_PREFIX}${testId}`;
}

/**
 * Read the stored variant for a key, preferring real storage.
 *
 * @input  key — full storage key (`ab:<testId>`)
 * @output the stored value, or null when nothing is stored anywhere
 * @important Caller must have checked that `window` exists.
 */
function readStored(key: string): string | null {
  try {
    const stored = window.localStorage.getItem(key);
    if (stored !== null) return stored;
  } catch {
    // Storage blocked — fall through to the in-memory copy.
  }
  return memoryStore.get(key) ?? null;
}

/**
 * Store the variant in both the in-memory map and localStorage.
 *
 * @input  key — full storage key; value — the assigned variant
 * @output void
 * @important The in-memory write comes FIRST on purpose: when storage
 *            throws, the assignment must still survive the page.
 */
function writeStored(key: string, value: string): void {
  memoryStore.set(key, value);
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Storage blocked — the in-memory copy above is the whole assignment.
  }
}

/** Six months: long enough that a returning visitor keeps their variant. */
const COOKIE_MAX_AGE_SECONDS = 180 * 24 * 60 * 60;

/**
 * Mirror the assignment into a cookie the server can read.
 *
 * @input  testId, value — the resolved variant
 * @output void
 * @important Best-effort like the storage write: a browser that refuses
 *            cookies simply falls back to the client-side swap.
 */
function writeCookie(testId: string, value: string): void {
  try {
    document.cookie =
      `ab_${testId}=${encodeURIComponent(value)}; path=/; max-age=${COOKIE_MAX_AGE_SECONDS}; samesite=lax`;
  } catch {
    // No document, or cookies blocked — the server will render the control.
  }
}

/**
 * Read the assignment back out of the cookie.
 *
 * @input  testId — the same identifier writeCookie used
 * @output the stored variant, or null when there is no cookie for this test
 * @important Not validated here: getVariant checks the value against the
 *            offered list, exactly as it does for a localStorage value, so a
 *            cookie naming a retired variant is re-rolled rather than kept.
 */
function readCookie(testId: string): string | null {
  try {
    const match = document.cookie.match(
      new RegExp(`(?:^|; )ab_${testId}=([^;]*)`),
    );
    return match ? decodeURIComponent(match[1]) : null;
  } catch {
    // No document, or cookies blocked — behave as "nothing stored".
    return null;
  }
}

/**
 * Read a QA override from the URL: `?ab_<testId>=<variant>`.
 *
 * @input  testId, variants — the override is accepted only when it names one
 *         of the offered variants
 * @output the forced variant, or null when absent or unknown
 * @important An unknown value is ignored rather than honoured: a typo in a
 *            QA link must not produce a variant nobody implemented.
 */
function readForcedFromUrl(testId: string, variants: readonly string[]): string | null {
  try {
    const forced = new URLSearchParams(window.location.search).get(`ab_${testId}`);
    return forced !== null && variants.includes(forced) ? forced : null;
  } catch {
    return null;
  }
}

/**
 * Resolve the variant this browser is assigned for a test.
 *
 * Order: URL override → localStorage → assignment cookie → uniform random
 * pick. Whatever answers is written to both stores, so the two never drift
 * apart for longer than one page view.
 *
 * @param testId   Stable test identifier, e.g. "leadform". Used as the
 *                 storage key suffix and as the URL parameter suffix.
 * @param variants The offered variants, control FIRST (it is what the
 *                 server renders).
 * @returns The assigned variant; `""` for an empty list.
 *
 * @example
 *   // In a client component, after mount:
 *   const variant = getVariant("leadform", ["A", "B", "B-nobudget", "C"]);
 */
export function getVariant(testId: string, variants: readonly string[]): string {
  if (variants.length === 0) return "";
  const control = variants[0];

  // SSR: no window, no storage — the server always renders the control.
  if (typeof window === "undefined") return control;

  const key = storageKeyFor(testId);

  const forced = readForcedFromUrl(testId, variants);
  if (forced !== null) {
    writeStored(key, forced);
    writeCookie(testId, forced);
    return forced;
  }

  const stored = readStored(key);
  if (stored !== null && variants.includes(stored)) {
    // Re-written on every read so a visitor assigned before the cookie
    // existed (or whose cookie expired) gets it back.
    writeCookie(testId, stored);
    return stored;
  }

  // Storage said nothing usable — the cookie may still hold the assignment
  // the server just rendered from. Honouring it here is what keeps one
  // visitor on one form across the homepage and every tail (invariant B-I6).
  const fromCookie = readCookie(testId);
  if (fromCookie !== null && variants.includes(fromCookie)) {
    writeStored(key, fromCookie);
    return fromCookie;
  }

  const picked = variants[Math.floor(Math.random() * variants.length)];
  writeStored(key, picked);
  writeCookie(testId, picked);
  return picked;
}

/**
 * Pin a variant for this browser without going through the URL.
 *
 * @param testId  Same identifier as in getVariant.
 * @param variant The variant to store; not validated against any list —
 *                the caller decides what is legal (getVariant re-rolls an
 *                unknown value on the next read anyway).
 * @returns The variant passed in, so the call can be used inline.
 *
 * @example
 *   // QA console: force the soft form and reload.
 *   forceVariant("leadform", "C");
 */
export function forceVariant(testId: string, variant: string): string {
  if (typeof window === "undefined") return variant;
  writeStored(storageKeyFor(testId), variant);
  writeCookie(testId, variant);
  return variant;
}
